import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import type { PropostaItem, PropostaItemTipo } from '@/types'
import { isDesenvolvimentoPlan, type CatalogPlan } from '@/utils/planCatalog'
import {
  buildPlanEditorItem,
  resolvePlanEditorScope,
  type PlanEditorCategoryGroup,
  type PlanEditorServiceItem,
} from './mergePlanServicesWithTemplate'

export function usePlanEditor(
  open: boolean,
  plan: PropostaItem | null,
  onSave: (updated: PropostaItem) => void,
  onClose: () => void,
  catalogPlans: CatalogPlan[] = [],
  guided = false,
) {
  const [activeTab, setActiveTab] = useState<'details' | 'scope'>('details')
  const [descricao, setDescricao] = useState('')
  const [tipo, setTipo] = useState<PropostaItemTipo>('plano')
  const [quantidade, setQuantidade] = useState(1)
  const [duration, setDuration] = useState(12)
  const [pricingModel, setPricingModel] = useState<'recorrente' | 'setup'>('recorrente')
  const [valorTotal, setValorTotal] = useState(0)
  const [valorEntrada, setValorEntrada] = useState(0)
  const [numParcelas, setNumParcelas] = useState(12)
  const [valorParcela, setValorParcela] = useState(0)
  const [showBonus, setShowBonus] = useState(false)
  const [bonusText, setBonusText] = useState('')
  const [servicesList, setServicesList] = useState<PlanEditorCategoryGroup[]>([])
  const [supportPricingMode, setSupportPricingMode] =
    useState<'FIXO_2000' | 'BASE_500_MAIS_1_FATURAMENTO'>('FIXO_2000')

  const isDesenvolvimento = useMemo(
    () => (plan ? isDesenvolvimentoPlan(plan, catalogPlans) : false),
    [plan, catalogPlans],
  )

  const planIdentity = plan?.id || plan?.catalogPlanId || plan?.descricao || ''

  useEffect(() => {
    if (!open || !plan) return
    setDescricao(plan.descricao || plan.name || '')
    setTipo('plano')
    setQuantidade(plan.quantidade || 1)
    setDuration(guided ? 12 : plan.duration || 12)

    const pr = plan.pricing || {
      total: plan.valorUnitario * (plan.duration || 12),
      entry: 0,
      installments: plan.duration || 12,
      installmentValue: plan.valorUnitario,
      recorrente: true,
    }

    setPricingModel('recorrente')
    setValorTotal(pr.total || plan.valorUnitario * (plan.duration || 12))
    setValorEntrada(pr.entry || 0)
    setNumParcelas(pr.installments || plan.duration || 12)
    const loadedParcela = Number(pr.installmentValue || plan.valorUnitario || 0)
    const loadedMode = guided
      ? 'FIXO_2000'
      : pr.supportPricingMode === 'BASE_500_MAIS_1_FATURAMENTO'
        ? 'BASE_500_MAIS_1_FATURAMENTO'
        : 'FIXO_2000'
    setSupportPricingMode(loadedMode)
    setValorParcela(loadedParcela > 0 ? loadedParcela : loadedMode === 'BASE_500_MAIS_1_FATURAMENTO' ? 500 : 2000)
    setShowBonus(plan.showBonus || false)
    setBonusText(plan.bonus || '')
    setActiveTab('details')
    // Hidrata só ao abrir o plano. Catálogo novo não pode apagar a modalidade escolhida.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planIdentity, guided])

  useEffect(() => {
    if (!open || !plan) return
    setServicesList(resolvePlanEditorScope(plan, catalogPlans))
  }, [open, plan, planIdentity, catalogPlans])

  useEffect(() => {
    setTipo('plano')
    setPricingModel('recorrente')
  }, [])

  useEffect(() => {
    if (pricingModel === 'recorrente') {
      setValorTotal(valorEntrada + duration * valorParcela)
      setNumParcelas(duration)
    } else if (numParcelas > 0) {
      const calculatedParcela = Math.max(0, (valorTotal - valorEntrada) / numParcelas)
      setValorParcela(Number(calculatedParcela.toFixed(2)))
    }
  }, [pricingModel, valorEntrada, duration, valorParcela, valorTotal, numParcelas])

  const handleSave = useCallback(() => {
    if (!plan) return
    if (!descricao.trim()) {
      toast.error('Informe o nome do plano')
      return
    }

    const resolvedTipo: PropostaItemTipo = 'plano'
    const resolvedPricingModel = 'recorrente'

    onSave(
      buildPlanEditorItem(plan, {
        descricao,
        tipo: resolvedTipo,
        quantidade,
        duration,
        pricingModel: resolvedPricingModel,
        valorParcela,
        valorTotal,
        valorEntrada,
        numParcelas,
        showBonus,
        bonusText,
        servicesList,
        supportPricingMode,
        monthlyFixedValue: 2000,
        monthlyBaseValue: 500,
        revenueSharePercent: supportPricingMode === 'BASE_500_MAIS_1_FATURAMENTO' ? 1 : 0,
      }),
    )
    onClose()
  }, [
    plan,
    descricao,
    tipo,
    quantidade,
    duration,
    pricingModel,
    valorParcela,
    valorTotal,
    valorEntrada,
    numParcelas,
    showBonus,
    bonusText,
    servicesList,
    supportPricingMode,
    isDesenvolvimento,
    onSave,
    onClose,
  ])

  const updateServiceField = useCallback(
    (catIndex: number, svcIndex: number, field: keyof PlanEditorServiceItem, value: string | boolean) => {
      setServicesList((prev) =>
        prev.map((cat, cIdx) => {
          if (cIdx !== catIndex) return cat
          return {
            ...cat,
            services: cat.services.map((svc, sIdx) => {
              if (sIdx !== svcIndex) return svc
              const updated = { ...svc, [field]: value }
              if (field === 'isPartial' && value === true) updated.isIncluded = true
              if (field === 'quantity' && value !== '') updated.isIncluded = true
              return updated
            }),
          }
        }),
      )
    },
    [],
  )

  const toggleCategoryAll = useCallback((catIndex: number, checkAll: boolean) => {
    setServicesList((prev) =>
      prev.map((cat, cIdx) => {
        if (cIdx !== catIndex) return cat
        return {
          ...cat,
          services: cat.services.map((svc) => ({
            ...svc,
            isIncluded: checkAll,
            isPartial: checkAll ? svc.isPartial : false,
          })),
        }
      }),
    )
  }, [])

  const proposedTotal = (valorEntrada + duration * valorParcela) * quantidade

  return {
    isDesenvolvimento,
    activeTab,
    setActiveTab,
    descricao,
    setDescricao,
    tipo,
    setTipo,
    quantidade,
    duration,
    setDuration,
    pricingModel,
    setPricingModel,
    valorTotal,
    setValorTotal,
    valorEntrada,
    setValorEntrada,
    numParcelas,
    setNumParcelas,
    valorParcela,
    setValorParcela,
    showBonus,
    setShowBonus,
    bonusText,
    setBonusText,
    servicesList,
    handleSave,
    updateServiceField,
    toggleCategoryAll,
    proposedTotal,
    supportPricingMode,
    setSupportPricingMode,
  }
}
